<script>
	import { createEventDispatcher } from 'svelte';
	import Icon from '../icons/Icon.svelte';

	const dispatch = createEventDispatcher();

	export let activeChat = null;
	export let activeChatId = '';
	export let currentUserId = null;
	export let now = Date.now();
	export let showCustomerDrawer = false;
	export let loadingHistory = false;
	export let loadingHistoryPhone = '';
	export let isAiTyping = false;
	export let isSendingMessage = false;
	export let pendingMediaUrl = '';
	export let messageInput = '';
	export let onNotify = (msg, type, duration) => {};

	export let quickMacros = [
		{ label: "Cardápio", text: "Segue o nosso cardápio digital completo para você escolher suas delícias: https://matafome.com.br/cardapio", icon: "book-open" },
		{ label: "Chave PIX", text: "Nossa chave PIX oficial é o CNPJ/Telefone cadastrado. Ao realizar a transferência, favor enviar o comprovante aqui no chat!", icon: "key" },
		{ label: "Previsão (40-50m)", text: "Seu pedido está em produção com todo carinho! A previsão média de entrega é de 40 a 50 minutos.", icon: "clock" },
		{ label: "Pedir Endereço", text: "Poderia por gentileza nos confirmar seu endereço completo com número, bairro e ponto de referência?", icon: "map-pin" },
		{ label: "Saiu p/ Entrega", text: "Boas notícias! O entregador acabou de sair com o seu pedido a caminho do seu endereço.", icon: "bike" }
	];

	export let getInitials = (name) => {
		if (!name) return "CL";
		const clean = name.replace(/[^\w\s]/gi, '').trim();
		const parts = clean.split(/\s+/).filter(Boolean);
		if (parts.length === 0) return "CL";
		if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
		return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
	};

	export let formatPhoneNumber = (phone) => {
		if (!phone) return "";
		const clean = phone.replace(/\D/g, "");
		if (clean.length === 13 && clean.startsWith("55")) {
			return `+55 (${clean.slice(2, 4)}) ${clean.slice(4, 9)}-${clean.slice(9)}`;
		}
		if (clean.length === 12 && clean.startsWith("55")) {
			return `+55 (${clean.slice(2, 4)}) ${clean.slice(4, 8)}-${clean.slice(8)}`;
		}
		if (clean.length === 11) {
			return `(${clean.slice(0, 2)}) ${clean.slice(2, 7)}-${clean.slice(7)}`;
		}
		if (clean.length === 10) {
			return `(${clean.slice(0, 2)}) ${clean.slice(2, 6)}-${clean.slice(6)}`;
		}
		return phone;
	};

	export let formatWaitingTime = (humanRequestedAt, currentNow = now) => {
		if (!humanRequestedAt) return "Aguardando";
		const requestedTime = new Date(humanRequestedAt).getTime();
		if (isNaN(requestedTime)) return "Aguardando";
		const diffMinutes = Math.floor(Math.max(0, currentNow - requestedTime) / 60000);
		if (diffMinutes < 1) return "Aguardando agora";
		if (diffMinutes === 1) return "Aguardando há 1 min";
		return `Aguardando há ${diffMinutes} min`;
	};

	export let formatWhatsAppText = (raw) => {
		if (!raw) return "";
		let text = String(raw)
			.replace(/&/g, "&amp;")
			.replace(/</g, "&lt;")
			.replace(/>/g, "&gt;");

		text = text.replace(/\*([^\*\n]+)\*/g, '<strong class="wa-bold">$1</strong>');
		text = text.replace(/_([^\_\n]+)_/g, '<em class="wa-italic">$1</em>');
		text = text.replace(/~([^~\n]+)~/g, '<del class="wa-strike">$1</del>');
		text = text.replace(/\n/g, '<br/>');

		return text;
	};

	function handleBackToList() {
		activeChatId = '';
		dispatch('backToList');
	}

	function handleToggleCustomerBot() {
		dispatch('toggleBot');
	}

	function handleToggleCustomerDrawer() {
		showCustomerDrawer = !showCustomerDrawer;
		dispatch('toggleCustomerDrawer', showCustomerDrawer);
	}

	function handleLoadChatHistory(cursorObj) {
		dispatch('loadHistory', cursorObj);
	}

	function handleRetryFailedMessage(msg) {
		dispatch('retryMessage', msg);
	}

	function handleApplyMacro(text) {
		if (isSendingMessage) return;
		messageInput = text;
	}

	function handlePromptMediaAttachment() {
		if (isSendingMessage) return;
		const url = prompt("Cole ou digite a URL da imagem (JPG/PNG/WebP):", pendingMediaUrl || "");
		if (url !== null) {
			pendingMediaUrl = url.trim();
		}
	}

	function handleSendMessageClick() {
		if (isSendingMessage) return;
		dispatch('sendMessage');
	}
</script>

<main class="wa-conversation">
				{#if activeChat}
					<!-- Header do Contato Ativo -->
					<div class="wa-chat-header">
						<!-- Botão Voltar Mobile -->
						<button
							type="button"
							class="btn-back-to-list"
							on:click={handleBackToList}
							title="Voltar para a lista de conversas"
							aria-label="Voltar para a lista de conversas"
						>
							<Icon name="chevron-down" size={16} />
							<span>Conversas</span>
						</button>

						<div class="chat-contact-box">
							<div class="avatar-header" class:initials-avatar={!activeChat.avatarUrl}>
								{#if activeChat.avatarUrl}
									<img src="{activeChat.avatarUrl}" alt="{activeChat.name}" class="avatar-photo-img" on:error={() => { activeChat.avatarUrl = null; }} />
								{:else}
									{getInitials(activeChat.name)}
								{/if}
							</div>
							<div class="contact-meta">
								<div class="name-row">
									<h4>{activeChat.name}</h4>
									{#if activeChat.phone}
										<span class="contact-phone">{formatPhoneNumber(activeChat.phone)}</span>
									{/if}
								</div>
								<div class="contact-status-sub">
									{#if activeChat.botActive === true && activeChat.needsHuman !== true}
										<span class="status-chip bot" title="IA respondendo automaticamente">
											<Icon name="bot" size={12} />
											<span>IA</span>
										</span>
									{:else if activeChat.status === 'human_requested' || activeChat.needsHuman === true}
										<span class="status-chip danger" title={activeChat.humanRequestedAt ? formatWaitingTime(activeChat.humanRequestedAt, now) : 'Aguardando atendimento'}>
											<Icon name="clock" size={12} />
											<span>Aguardando atendimento{#if activeChat.humanRequestedAt} <span class="header-wait-time">({formatWaitingTime(activeChat.humanRequestedAt, now)})</span>{/if}</span>
										</span>
									{:else if activeChat.status === 'human_active'}
										{#if !activeChat.assignedOperatorId}
											<span class="status-chip human" title="Atendimento humano ativo">
												<Icon name="user" size={12} />
												<span>Atendimento Humano</span>
											</span>
										{:else if currentUserId && activeChat.assignedOperatorId === currentUserId}
											<span class="status-chip operator-mine" title="Você está responsável por este atendimento">
												<Icon name="user" size={12} />
												<span>Você está atendendo</span>
											</span>
										{:else}
											<span class="status-chip operator-other" title={`Atendido por ${activeChat.assignedOperatorName || 'Outro operador'}`}>
												<Icon name="users" size={12} />
												<span class="header-operator-name">Atendido por {activeChat.assignedOperatorName || 'Operador'}</span>
											</span>
										{/if}
									{:else}
										<span class="status-chip bot" title="IA respondendo automaticamente">
											<Icon name="bot" size={12} />
											<span>IA</span>
										</span>
									{/if}
								</div>
							</div>
						</div>

						<div class="header-actions-group">
							<!-- Alternador de Modo de Atendimento (IA vs Humano) -->
							{#if activeChat.needsHuman || !activeChat.botActive}
								<button
									class="btn-mode-switch to-bot"
									on:click={handleToggleCustomerBot}
									title="Devolver para o Robô de IA responder este cliente"
								>
									<Icon name="bot" size={14} />
									<span>Devolver para IA</span>
								</button>
							{:else}
								<button
									class="btn-mode-switch to-human"
									on:click={handleToggleCustomerBot}
									title="Pausar a IA e assumir o atendimento manualmente"
								>
									<Icon name="user" size={14} />
									<span>Assumir Chat (Pausar IA)</span>
								</button>
							{/if}

							<!-- Botão do Dossiê do Cliente -->
							<button
								class="btn-dossier-toggle"
								class:active={showCustomerDrawer}
								on:click={handleToggleCustomerDrawer}
								title="Ver dados e pedidos do cliente"
							>
								<Icon name="user" size={14} />
								<span>Dossiê</span>
							</button>
						</div>
					</div>

					<!-- Container das Mensagens -->
					<div class="messages-container">
						<div class="chat-watermark-date">
							<span>Mensagens sincronizadas em tempo real com o WhatsApp do restaurante</span>
						</div>

						{#if activeChat && (activeChat._hasMore || (loadingHistory && loadingHistoryPhone === (activeChat.phone || '').replace(/\D/g, '')))}
							<div class="load-older-messages-bar" style="display: flex; justify-content: center; margin: 8px 0;">
								<button
									type="button"
									class="btn-load-older"
									style="background: #202c33; border: 1px solid #374045; color: #00a884; padding: 6px 14px; border-radius: 16px; font-size: 12px; font-weight: 500; cursor: pointer; display: flex; align-items: center; gap: 6px;"
									disabled={loadingHistory}
									on:click={() => handleLoadChatHistory({ before: activeChat._nextCursor })}
								>
									{#if loadingHistory}
										<Icon name="clock" size={13} />
										<span>Carregando histórico...</span>
									{:else}
										<Icon name="arrow-up" size={13} />
										<span>Carregar mensagens anteriores</span>
									{/if}
								</button>
							</div>
						{/if}

						{#each (activeChat.messages || []) as msg}
							<div class="message-row" class:outgoing={msg.fromMe} class:incoming={!msg.fromMe}>
								<div class="message-bubble"
									class:bot-bubble={msg.isBot}
									class:operator-bubble={msg.isOperator}
									class:bubble-sending={msg.status === 'sending'}
									class:bubble-warning={msg.status === 'warning'}
									class:bubble-failed={msg.status === 'failed'}
									class:bubble-unknown={msg.status === 'unknown'}
								>
									{#if msg.isBot}
										<div class="bubble-sender-tag bot">
											<Icon name="bot" size={11} />
											<span>MataFome AI</span>
										</div>
									{:else if msg.isOperator}
										<div class="bubble-sender-tag operator">
											<Icon name="user" size={11} />
											<span>Atendente</span>
										</div>
									{/if}

									{#if msg.mediaUrl || msg.imageUrl}
										<div class="bubble-media-wrap">
											<img src="{msg.mediaUrl || msg.imageUrl}" alt="Mídia da conversa" class="bubble-media-img" loading="lazy" on:error={(e) => { e.currentTarget.style.display = 'none'; }} />
										</div>
									{/if}

									{#if msg.text}
										<div class="bubble-content-text">
											{@html formatWhatsAppText(msg.text)}
										</div>
									{/if}

									{#if msg.status === 'warning'}
										<div class="bubble-status-banner warning">
											<span>{msg.warning || "Enviado ao WhatsApp, histórico local pendente."}</span>
										</div>
									{:else if msg.status === 'failed'}
										<div class="bubble-status-banner failed">
											<span>{msg.error || "Falha no envio."}</span>
											<button type="button" class="btn-retry-inline" on:click={() => handleRetryFailedMessage(msg)} title="Reenviar mensagem">
												Reenviar
											</button>
										</div>
									{:else if msg.status === 'unknown'}
										<div class="bubble-status-banner unknown">
											<span>{msg.error || "Envio incerto. Verifique a conexão antes de reenviar."}</span>
										</div>
									{/if}

									<div class="bubble-meta">
										<span class="bubble-time">{msg.time || ''}</span>
										{#if msg.fromMe}
											{#if msg.status === 'sending'}
												<span class="bubble-status sending" title="Enviando para o WhatsApp...">🕒</span>
											{:else if msg.status === 'warning'}
												<span class="bubble-status warning" title={msg.warning || "Enviado ao WhatsApp, histórico local pendente"}>⚠️</span>
											{:else if msg.status === 'failed'}
												<span class="bubble-status failed" title={msg.error || "Falha conclusiva no envio"}>❌</span>
											{:else if msg.status === 'unknown'}
												<span class="bubble-status unknown" title="Resultado do envio não confirmado">❓</span>
											{:else}
												<span class="bubble-status sent" title="Enviado ao WhatsApp">✓</span>
											{/if}
										{/if}
									</div>
								</div>
							</div>
						{/each}

						{#if isAiTyping}
							<div class="message-row outgoing">
								<div class="message-bubble bot-bubble typing-bubble">
									<div class="bubble-sender-tag bot">
										<Icon name="bot" size={11} />
										<span>MataFome AI</span>
									</div>
									<div class="typing-dots-wrap">
										<span class="typing-dot"></span>
										<span class="typing-dot"></span>
										<span class="typing-dot"></span>
										<small>Consultando cardápio & digitando...</small>
									</div>
								</div>
							</div>
						{/if}
					</div>

					<!-- Área do Compositor de Mensagens & Macros -->
					<div class="wa-composer-section">
						<!-- Indicador de Modo de Envio -->
						<div class="composer-mode-bar">
							{#if activeChat.botActive && !activeChat.needsHuman}
								<span class="mode-text bot">
									<Icon name="bot" size={14} /> <strong>IA Ativa:</strong> O robô responde automaticamente. Digite para assumir o controle pelo atendente.
								</span>
							{:else}
								<span class="mode-text human">
									<Icon name="user" size={14} /> <strong>Modo Atendente:</strong> Suas mensagens serão enviadas diretamente ao cliente.
								</span>
							{/if}
						</div>

						<!-- Barra de Macros (Respostas Rápidas) -->
						<div class="quick-macros-strip">
							<span class="macros-label">Atalhos:</span>
							{#each quickMacros as macro}
								<button
									type="button"
									class="macro-chip-btn"
									disabled={isSendingMessage}
									on:click={() => handleApplyMacro(macro.text)}
									title="Inserir texto: {macro.text}"
								>
									<Icon name={macro.icon} size={11} />
									<span>{macro.label}</span>
								</button>
							{/each}
						</div>

						{#if pendingMediaUrl}
							<div class="pending-media-preview-bar">
								<img src="{pendingMediaUrl}" alt="Preview da imagem" class="pending-media-thumb" on:error={() => { pendingMediaUrl = ""; if (onNotify) onNotify("URL de imagem inválida.", "error", 3); }} />
								<div class="pending-media-info">
									<span>Imagem anexada para envio</span>
									<button type="button" class="btn-remove-media" on:click={() => pendingMediaUrl = ""} title="Remover imagem">
										<Icon name="x" size={14} />
									</button>
								</div>
							</div>
						{/if}

						<!-- Linha de Input e Botão de Envio -->
						<div class="wa-input-row">
							<button
								type="button"
								class="btn-attach-media"
								class:has-media={!!pendingMediaUrl}
								disabled={isSendingMessage}
								on:click={handlePromptMediaAttachment}
								title="Anexar imagem com legenda para o cliente"
								aria-label="Anexar imagem"
							>
								<Icon name="camera" size={16} />
							</button>
							<input
								type="text"
								class="wa-main-input"
								placeholder={pendingMediaUrl ? "Digite uma legenda para a imagem (opcional)..." : "Digite sua mensagem para o cliente..."}
								bind:value={messageInput}
								disabled={isSendingMessage}
								on:keydown={(e) => {
									if (e.key === "Enter" && !isSendingMessage) {
										handleSendMessageClick();
									}
								}}
							/>
							<button
								class="btn-send-primary"
								on:click={handleSendMessageClick}
								disabled={isSendingMessage || (!messageInput.trim() && !pendingMediaUrl)}
								title="Enviar mensagem para o WhatsApp do cliente"
								aria-label="Enviar mensagem"
							>
								<Icon name="send" size={16} />
							</button>
						</div>
					</div>
				{:else}
					<!-- Estado Vazio: Nenhuma Conversa Selecionada -->
					<div class="wa-empty-chat-state">
						<div class="wa-empty-icon-box">
							<Icon name="whatsapp" size={48} />
						</div>
						<h3>Central de Atendimento Inteligente</h3>
						<p>Selecione uma conversa na lista lateral para visualizar mensagens e interagir com o cliente.</p>
						<span class="empty-subtext">As mensagens recebidas pelo WhatsApp oficial são processadas pela IA e sincronizadas em tempo real.</span>
					</div>
				{/if}
			</main>

<style>
/* CHAT / ÁREA DE CONVERSA */
	.wa-conversation {
		display: flex;
		flex-direction: column;
		height: 100%;
		background: #F8FAFC;
		position: relative;
		overflow: hidden;
	}

	.wa-chat-header {
		padding: 12px 20px;
		background: var(--neutral-surface, #FFFFFF);
		border-bottom: 1px solid var(--neutral-border, #E2E8F0);
		display: flex;
		justify-content: space-between;
		align-items: center;
		flex-shrink: 0;
		gap: 12px;
	}

	.btn-back-to-list {
		display: none;
		align-items: center;
		gap: 4px;
		background: transparent;
		border: none;
		color: var(--brand-primary, #FF6A00);
		font-size: 13px;
		font-weight: 700;
		cursor: pointer;
		padding: 4px;
		flex-shrink: 0;
	}

	.chat-contact-box {
		display: flex;
		align-items: center;
		gap: 10px;
		min-width: 0;
		flex: 1;
	}

	.avatar-header {
		width: 40px;
		height: 40px;
		border-radius: 50%;
		flex-shrink: 0;
	}

	.contact-meta {
		display: flex;
		flex-direction: column;
		gap: 2px;
		min-width: 0;
	}

	.name-row {
		display: flex;
		align-items: center;
		gap: 8px;
		min-width: 0;
	}

	.name-row h4 {
		margin: 0;
		font-size: 15px;
		font-weight: 800;
		color: var(--text-primary, #0F172A);
		max-width: 100%;
		min-width: 0;
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
	}

	.contact-phone {
		font-size: 12px;
		color: var(--text-secondary, #64748B);
		font-weight: 500;
		flex-shrink: 0;
	}

	.status-chip {
		display: inline-flex;
		align-items: center;
		gap: 5px;
		font-size: 11.5px;
		font-weight: 600;
		transition: all 0.2s ease;
	}

	.status-chip.danger { color: #DC2626; }
	.status-chip.human { color: #2563EB; }
	.status-chip.operator-mine { color: #1D4ED8; font-weight: 700; }
	.status-chip.operator-other { color: #475569; }
	.status-chip.bot { color: #16A34A; }
	.header-wait-time { font-weight: 500; opacity: 0.9; }

	.header-operator-name {
		max-width: 180px;
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
		display: inline-block;
		vertical-align: middle;
	}

	.header-actions-group {
		display: flex;
		align-items: center;
		gap: 8px;
		flex-shrink: 0;
	}

	.btn-mode-switch {
		display: inline-flex;
		align-items: center;
		gap: 6px;
		padding: 7px 12px;
		border-radius: var(--radius-md, 8px);
		font-size: 12px;
		font-weight: 700;
		cursor: pointer;
		border: 1px solid transparent;
		transition: all 0.15s ease;
	}

	.btn-mode-switch.to-human {
		background: #EFF6FF;
		color: #2563EB;
		border-color: #BFDBFE;
	}

	.btn-mode-switch.to-human:hover {
		background: #2563EB;
		color: #FFFFFF;
	}

	.btn-mode-switch.to-bot {
		background: #DCFCE7;
		color: #16A34A;
		border-color: #BBF7D0;
	}

	.btn-mode-switch.to-bot:hover {
		background: #16A34A;
		color: #FFFFFF;
	}

	.btn-dossier-toggle {
		display: inline-flex;
		align-items: center;
		gap: 6px;
		background: var(--neutral-surface-hover, #F1F5F9);
		border: 1px solid var(--neutral-border, #CBD5E1);
		padding: 7px 12px;
		border-radius: var(--radius-md, 8px);
		font-size: 12px;
		font-weight: 700;
		color: var(--text-secondary, #475569);
		cursor: pointer;
		transition: all 0.15s ease;
	}

	.btn-dossier-toggle:hover {
		background: #E2E8F0;
		color: var(--text-primary, #0F172A);
	}

	.btn-dossier-toggle.active {
		background: var(--brand-primary, #FF6A00);
		color: #FFFFFF;
		border-color: var(--brand-primary, #FF6A00);
	}

	/* CONTAINER DE MENSAGENS */
	.messages-container {
		flex: 1;
		overflow-y: auto;
		padding: 20px;
		display: flex;
		flex-direction: column;
		gap: 12px;
		background: #F1F5F9;
	}

	.chat-watermark-date {
		text-align: center;
		margin-bottom: 8px;
	}

	.chat-watermark-date span {
		background: rgba(15, 23, 42, 0.06);
		color: var(--text-secondary, #64748B);
		padding: 4px 12px;
		border-radius: var(--radius-full, 9999px);
		font-size: 11px;
		font-weight: 600;
	}

	.message-row {
		display: flex;
		width: 100%;
	}

	.message-row.incoming {
		justify-content: flex-start;
	}

	.message-row.outgoing {
		justify-content: flex-end;
	}

	.message-bubble {
		max-width: 65%;
		padding: 10px 14px;
		border-radius: 14px;
		box-shadow: 0 1px 2px rgba(15, 23, 42, 0.06);
		position: relative;
		display: flex;
		flex-direction: column;
		gap: 4px;
	}

	.message-row.incoming .message-bubble {
		background: #FFFFFF;
		color: var(--text-primary, #0F172A);
		border-bottom-left-radius: 4px;
		border: 1px solid var(--neutral-border, #E2E8F0);
	}

	.message-row.outgoing .message-bubble {
		background: #DCF8C6;
		color: #0F172A;
		border-bottom-right-radius: 4px;
		border: 1px solid #C8E6C9;
	}

	.message-row.outgoing .message-bubble.bot-bubble {
		background: #FAF5FF;
		border: 1px solid #E9D5FF;
	}

	.message-row.outgoing .message-bubble.operator-bubble {
		background: #FFF7ED;
		border: 1px solid #FFEDD5;
	}

	.bubble-sender-tag {
		display: inline-flex;
		align-items: center;
		gap: 4px;
		font-size: 10.5px;
		font-weight: 800;
		text-transform: uppercase;
		margin-bottom: 2px;
	}

	.bubble-sender-tag.bot { color: #7E22CE; }
	.bubble-sender-tag.operator { color: #C2410C; }

	.bubble-content-text {
		font-size: 13.5px;
		line-height: 1.5;
		word-break: break-word;
	}

	.bubble-media-wrap {
		margin-bottom: 6px;
		border-radius: 8px;
		overflow: hidden;
		max-width: 280px;
	}

	.bubble-media-img {
		width: 100%;
		max-height: 240px;
		object-fit: cover;
		display: block;
		border-radius: 8px;
		background: #E2E8F0;
	}

	:global(.wa-bold) { font-weight: 800; }
	:global(.wa-italic) { font-style: italic; }
	:global(.wa-strike) { text-decoration: line-through; }

	.bubble-meta {
		display: flex;
		align-items: center;
		justify-content: flex-end;
		gap: 4px;
		margin-top: 2px;
	}

	.bubble-time {
		font-size: 10px;
		color: var(--text-muted, #94A3B8);
	}

	.message-bubble.bubble-sending {
		opacity: 0.85;
	}

	.message-bubble.bubble-warning {
		border-color: #FCD34D !important;
	}

	.message-bubble.bubble-failed {
		border-color: #F87171 !important;
		background: #FEF2F2 !important;
	}

	.message-bubble.bubble-unknown {
		border-color: #CBD5E1 !important;
		border-style: dashed !important;
	}

	.bubble-status {
		font-size: 11px;
		font-weight: 800;
		display: inline-flex;
		align-items: center;
	}

	.bubble-status.sending {
		color: var(--text-muted, #94A3B8);
	}

	.bubble-status.sent {
		color: #059669;
	}

	.bubble-status.warning {
		color: #D97706;
	}

	.bubble-status.failed {
		color: #DC2626;
	}

	.bubble-status.unknown {
		color: #64748B;
	}

	.bubble-status-banner {
		font-size: 10.5px;
		padding: 3px 6px;
		border-radius: 4px;
		margin-top: 4px;
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 6px;
		line-height: 1.3;
	}

	.bubble-status-banner.warning {
		background: #FEF3C7;
		color: #92400E;
		border: 1px solid #FDE68A;
	}

	.bubble-status-banner.failed {
		background: #FEE2E2;
		color: #991B1B;
		border: 1px solid #FECACA;
	}

	.bubble-status-banner.unknown {
		background: #F1F5F9;
		color: #475569;
		border: 1px solid #E2E8F0;
	}

	.btn-retry-inline {
		background: #DC2626;
		color: #FFFFFF;
		border: none;
		border-radius: 3px;
		padding: 2px 7px;
		font-size: 10px;
		font-weight: 700;
		cursor: pointer;
		white-space: nowrap;
		transition: background 0.15s ease;
	}

	.btn-retry-inline:hover {
		background: #B91C1C;
	}

	.typing-dots-wrap {
		display: flex;
		align-items: center;
		gap: 4px;
		padding: 4px 0;
	}

	.typing-dot {
		width: 6px;
		height: 6px;
		border-radius: 50%;
		background: #7E22CE;
		animation: typingPulse 1.2s infinite ease-in-out;
	}

	.typing-dot:nth-child(2) { animation-delay: 0.2s; }
	.typing-dot:nth-child(3) { animation-delay: 0.4s; }

	@keyframes typingPulse {
		0%, 100% { transform: scale(1); opacity: 0.4; }
		50% { transform: scale(1.4); opacity: 1; }
	}

	.typing-dots-wrap small {
		margin-left: 6px;
		font-size: 11px;
		color: #7E22CE;
		font-weight: 600;
	}

	/* COMPOSITOR */
	.wa-composer-section {
		background: var(--neutral-surface, #FFFFFF);
		border-top: 1px solid var(--neutral-border, #E2E8F0);
		padding: 10px 18px 14px 18px;
		display: flex;
		flex-direction: column;
		gap: 8px;
		flex-shrink: 0;
	}

	.composer-mode-bar {
		display: flex;
		align-items: center;
		font-size: 11.5px;
	}

	.mode-text {
		display: inline-flex;
		align-items: center;
		gap: 5px;
	}

	.mode-text.bot { color: #7E22CE; }
	.mode-text.human { color: #2563EB; }

	.quick-macros-strip {
		display: flex;
		align-items: center;
		gap: 6px;
		overflow-x: auto;
		padding-bottom: 2px;
	}

	.macros-label {
		font-size: 11px;
		font-weight: 700;
		color: var(--text-muted, #94A3B8);
		text-transform: uppercase;
		flex-shrink: 0;
	}

	.macro-chip-btn {
		display: inline-flex;
		align-items: center;
		gap: 4px;
		background: var(--neutral-surface-hover, #F1F5F9);
		border: 1px solid var(--neutral-border, #CBD5E1);
		color: var(--text-secondary, #475569);
		font-size: 11.5px;
		font-weight: 600;
		padding: 4px 10px;
		border-radius: var(--radius-full, 9999px);
		cursor: pointer;
		white-space: nowrap;
		transition: all 0.15s ease;
	}

	.macro-chip-btn:hover {
		background: #E2E8F0;
		color: var(--text-primary, #0F172A);
	}

	.wa-input-row {
		display: flex;
		align-items: center;
		gap: 10px;
	}

	.wa-main-input {
		flex: 1;
		min-width: 0;
		border: 1px solid var(--neutral-border, #CBD5E1);
		border-radius: var(--radius-md, 10px);
		padding: 10px 14px;
		font-size: 13.5px;
		color: var(--text-primary, #0F172A);
		outline: none;
		transition: border-color 0.15s ease;
	}

	.wa-main-input:focus {
		border-color: var(--brand-primary, #FF6A00);
	}

	.btn-send-primary {
		width: 42px;
		height: 42px;
		border-radius: var(--radius-md, 10px);
		background: var(--brand-primary, #FF6A00);
		color: #FFFFFF;
		border: none;
		display: flex;
		align-items: center;
		justify-content: center;
		cursor: pointer;
		transition: all 0.15s ease;
		flex-shrink: 0;
	}

	.btn-send-primary:hover:not(:disabled) {
		background: var(--brand-primary-hover, #E85A00);
		transform: scale(1.04);
	}

	.btn-send-primary:disabled {
		opacity: 0.5;
		cursor: not-allowed;
	}

	.btn-attach-media {
		width: 42px;
		height: 42px;
		border-radius: var(--radius-md, 10px);
		background: #F1F5F9;
		color: var(--text-secondary, #475569);
		border: 1px solid var(--neutral-border, #CBD5E1);
		display: flex;
		align-items: center;
		justify-content: center;
		cursor: pointer;
		transition: all 0.15s ease;
		flex-shrink: 0;
	}

	.btn-attach-media:hover {
		background: #E2E8F0;
		color: var(--text-primary, #0F172A);
	}

	.btn-attach-media.has-media {
		background: #FFF7ED;
		border-color: #FDBA74;
		color: #EA580C;
	}

	.pending-media-preview-bar {
		display: flex;
		align-items: center;
		gap: 10px;
		padding: 6px 12px;
		background: #FFF7ED;
		border: 1px solid #FFEDD5;
		border-radius: 8px;
		margin-bottom: 8px;
	}

	.pending-media-thumb {
		width: 36px;
		height: 36px;
		object-fit: cover;
		border-radius: 6px;
		border: 1px solid rgba(0, 0, 0, 0.1);
	}

	.pending-media-info {
		flex: 1;
		display: flex;
		align-items: center;
		justify-content: space-between;
		font-size: 12px;
		font-weight: 600;
		color: #9A3412;
	}

	.btn-remove-media {
		background: transparent;
		border: none;
		color: #C2410C;
		cursor: pointer;
		display: flex;
		align-items: center;
		justify-content: center;
		padding: 4px;
		border-radius: 4px;
	}

	.btn-remove-media:hover {
		background: #FED7AA;
	}

	.wa-empty-chat-state {
		flex: 1;
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		text-align: center;
		padding: 32px;
		gap: 8px;
		background: var(--neutral-bg, #F8FAFC);
	}

	.wa-empty-icon-box {
		width: 72px;
		height: 72px;
		border-radius: 50%;
		background: var(--brand-primary-soft, #FFF7ED);
		color: var(--brand-primary, #FF6A00);
		display: flex;
		align-items: center;
		justify-content: center;
		margin-bottom: 6px;
	}

	.wa-empty-chat-state h3 {
		margin: 0;
		font-size: 18px;
		font-weight: 800;
		color: var(--text-primary, #0F172A);
	}

	.wa-empty-chat-state p {
		margin: 0;
		font-size: 13.5px;
		color: var(--text-secondary, #64748B);
		max-width: 420px;
	}

	.empty-subtext {
		font-size: 12px;
		color: var(--text-muted, #94A3B8);
		max-width: 440px;
	}
	.initials-avatar {
		width: 40px;
		height: 40px;
		border-radius: 50%;
		background: #E2E8F0;
		color: #334155;
		font-size: 14px;
		font-weight: 800;
		display: flex;
		align-items: center;
		justify-content: center;
		position: relative;
		flex-shrink: 0;
	}
	
	.avatar-photo-img {
		width: 100%;
		height: 100%;
		border-radius: 50%;
		object-fit: cover;
		display: block;
		background: #E2E8F0;
		border: 1px solid rgba(0, 0, 0, 0.08);
	}
	

	@media (max-width: 768px) {
		.wa-chat-header {
			padding: 10px 12px;
			gap: 8px;
		}

		.messages-container {
			padding: 12px;
		}

		.wa-composer-section {
			padding: 8px 12px 12px 12px;
		}

		.btn-back-to-list {
			display: inline-flex;
		}
	}

	@media (max-width: 480px) {
		.btn-mode-switch span {
			max-width: 85px;
			white-space: nowrap;
			overflow: hidden;
			text-overflow: ellipsis;
		}

		.header-operator-name {
			max-width: 110px;
		}
	}
</style>
